import type { ReactNode } from 'react';
import kaeliuxLogo from '../../assets/images/brand/kaeliux-logo.webp';
import { cn } from '../../utils/cn';
import { FACTION_COPY, FACTION_ORDER } from '../../utils/factionCopy';
import { RACE_PORTRAITS, raceLabel } from '../../utils/raceAssets';
import { RACE_ACCENT_VAR, RACE_BG_TINT } from '../../utils/raceColors';

export interface AuthLayoutProps {
    /** The one-line promise under the wordmark. Differs per page. */
    tagline: string;
    /** The sign-in / sign-up card's contents. */
    children: ReactNode;
    /**
     * `task` (password reset, email link) hides the faction triad below `lg`:
     * on a phone the player came for the form, and the triad would push it
     * below the fold. `brand` (sign-in, sign-up) always shows it.
     */
    focus?: 'brand' | 'task';
}

/**
 * The shared frame behind `/login` and `/register`: an asymmetric split with
 * the wordmark and the three-faction triad on the left and the auth card on
 * the right, collapsing to a single column below `lg`. Both pages used to
 * carry their own near-identical copy of this chrome, which is how they
 * drifted apart (only one of them linked to the other).
 *
 * Faction names, epithets and tints come from the same sources the race
 * picker reads, so the two screens can never describe a faction differently.
 *
 * The page background is not this component's job — `App.tsx` mounts the
 * fixed `SpaceBackdrop` (`neutral` variant) behind every logged-out route.
 */
export default function AuthLayout({ tagline, children, focus = 'brand' }: AuthLayoutProps) {
    return (
        <div
            data-testid="auth-layout"
            className="relative flex min-h-[100dvh] flex-col px-4 py-8 sm:px-6 lg:px-10"
        >
            <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col justify-center gap-10 lg:grid lg:grid-cols-[1.05fr_minmax(380px,0.85fr)] lg:items-center lg:gap-16">
                <div className="flex flex-col gap-6">
                    <div className="flex flex-col gap-3">
                        <h1 className="animate-rise-in">
                            <img
                                src={kaeliuxLogo}
                                alt="Kaeliux"
                                width={640}
                                height={621}
                                decoding="async"
                                className="h-28 w-auto sm:h-36 lg:h-44"
                            />
                        </h1>
                        <p
                            className="animate-rise-in max-w-[44ch] text-body text-content-secondary"
                            style={{ animationDelay: '80ms' }}
                        >
                            {tagline}
                        </p>
                    </div>

                    <ul
                        data-testid="auth-faction-triad"
                        className={cn('grid grid-cols-3 gap-3 sm:gap-4', focus === 'task' && 'hidden lg:grid')}
                    >
                        {FACTION_ORDER.map((race, index) => (
                            <li
                                key={race}
                                data-testid={`auth-faction-${race}`}
                                className="animate-rise-in flex h-full flex-col items-center gap-2 rounded-tile border border-hairline bg-surface-chip px-2 py-3 text-center sm:px-3 sm:py-4"
                                style={{
                                    animationDelay: `${160 + index * 60}ms`,
                                    backgroundImage: `linear-gradient(${RACE_BG_TINT[race]}, ${RACE_BG_TINT[race]})`,
                                }}
                            >
                                <span className="relative flex h-28 w-full items-end justify-center lg:h-44">
                                    <span
                                        aria-hidden="true"
                                        className="absolute inset-x-1 bottom-0 h-20 rounded-tile opacity-50 lg:h-28"
                                        style={{
                                            background: `radial-gradient(62% 72% at 50% 100%, ${RACE_ACCENT_VAR[race]}, transparent 72%)`,
                                        }}
                                    />
                                    <img
                                        src={RACE_PORTRAITS[race]}
                                        alt=""
                                        decoding="async"
                                        className="relative h-28 w-auto object-contain lg:h-44"
                                    />
                                </span>
                                <span className="text-title text-content-primary">{raceLabel(race)}</span>
                                <span className="text-body text-content-muted">{FACTION_COPY[race].epithet}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="animate-rise-in w-full lg:justify-self-end" style={{ animationDelay: '120ms' }}>
                    {children}
                </div>
            </div>

            <footer className="mx-auto mt-10 w-full max-w-6xl text-center text-content-muted text-micro lg:text-left">
                <p>&copy; {new Date().getFullYear()} Kaeliux</p>
            </footer>
        </div>
    );
}
